import { BOVERKET_ID_PREFIX, ProductID } from '../models/product.model';
import { genericProductsLookup } from '../generic-products';
import { getElementProductCategory } from './element-category.helpers';
import {
  ElementCategoryID,
  ProductCategoryId,
} from '../models/element-categories.model';
import { guards } from './typeguards.helpers';
import { ConversionFactors } from '../models/unit.model';
import marketEstimateRows from '../data/product-market-estimates.data.json';

type MarketEstimateRow = {
  boverket_id: string;
  product_name: string;
  cost_k_final: string;
  'co2_diff_%_final': string;
};

type EstimateDefaults = { co2Diff: number; costK: number };

const NO_ESTIMATE: EstimateDefaults = { co2Diff: 0, costK: 0 };

/**
 * Configuration for market estimate data generation.
 * Defines the percentile range and fallback defaults for the data.
 */
type MarketEstimateDataConfig = {
  /** The percentile range that the min/max factor limits represent (e.g., P5-P95) */
  percentileRange: { min: number; max: number };
  /** Default estimate when product has no data and no category default */
  defaultEstimate: EstimateDefaults;
  /** Category-specific defaults when product has no data */
  categoryDefaults: Record<ProductCategoryId, EstimateDefaults>;
};

/**
 * Market estimate data configuration.
 * The percentileRange specifies what percentiles the CSV data's min/max values represent.
 */
const MARKET_ESTIMATE_CONFIG: MarketEstimateDataConfig = {
  percentileRange: { min: 5, max: 95 }, // this is important! it's must correspond to both the CSV data & these defaults.
  defaultEstimate: { co2Diff: 0.3, costK: 0.6 },
  categoryDefaults: {
    [ElementCategoryID.Concrete]: { co2Diff: 0.5, costK: 0.4 },
    [ElementCategoryID.Wood]: { co2Diff: 0.28, costK: 0.4 },
    [ElementCategoryID.Insulation]: { co2Diff: 0.25, costK: 0.4 },
    [ElementCategoryID.Metal]: { co2Diff: 0.5, costK: 0.45 },
    [ElementCategoryID.Gypsum]: { co2Diff: 0.3, costK: 0.4 },
    [ElementCategoryID.Ceramics]: { co2Diff: 0.35, costK: 0.6 },
    [ElementCategoryID.WindowsDoors]: { co2Diff: 0.45, costK: 0.8 },
    [ElementCategoryID.Labour]: NO_ESTIMATE,
    [ElementCategoryID.Energy]: NO_ESTIMATE,
    [ElementCategoryID.OtherProduct]: { co2Diff: 0.3, costK: 0.4 },
  },
};

/** The percentile range that market estimate limits represent */
export const DATA_PERCENTILE_RANGE = MARKET_ESTIMATE_CONFIG.percentileRange;

/** Get category defaults configuration */
export const getCategoryDefaults = (): Record<
  ProductCategoryId,
  EstimateDefaults
> => MARKET_ESTIMATE_CONFIG.categoryDefaults;

export type MarketEstimateDefaultField = 'co2_diff' | 'cost_k';

/** Limits for carbon and cost adjustments */
export type MarketEstimateLimits = {
  carbon: { min: number; max: number };
  cost: { min: number; max: number };
};

export type MarketEstimateResult =
  | {
      valid: true;
      limits: MarketEstimateLimits;
      defaultsApplied: MarketEstimateDefaultField[];
    }
  | {
      valid: false;
      reason: 'missing_co2e_baseline' | 'missing_cost_baseline';
    };

// Lazy-loaded cache for parsed market estimate data
let _marketEstimateData: Record<ProductID, MarketEstimateResult> | undefined;

/**
 * Parse market estimate data and build the lookup.
 * Lazily evaluated to avoid import-time side effects.
 */
const parseMarketEstimateData = (): Record<ProductID, MarketEstimateResult> => {
  if (_marketEstimateData) return _marketEstimateData;

  const data = marketEstimateRows as MarketEstimateRow[];

  const entries = data.map((row): [ProductID, MarketEstimateResult] => {
    const productId = `${BOVERKET_ID_PREFIX}${row.boverket_id}` as ProductID;
    const product = genericProductsLookup[productId] || {
      conversion_factors: {} as ConversionFactors,
    };

    const co2eBaseline = product.conversion_factors['co2e_A1-A3'];
    const costBaseline = product.conversion_factors['sek_A1-A3'];

    if (guards.is.undefined(co2eBaseline)) {
      return [productId, { valid: false, reason: 'missing_co2e_baseline' }];
    }
    if (guards.is.undefined(costBaseline)) {
      return [productId, { valid: false, reason: 'missing_cost_baseline' }];
    }

    const defaultsApplied: MarketEstimateDefaultField[] = [];
    const categoryDefaults =
      MARKET_ESTIMATE_CONFIG.categoryDefaults[
        getElementProductCategory(productId)
      ];

    const parsedCo2Diff = parseFloat(row['co2_diff_%_final']);
    const parsedCostK = parseFloat(row.cost_k_final);

    const co2Diff =
      guards.is.finiteNumber(parsedCo2Diff) && parsedCo2Diff > 0
        ? parsedCo2Diff
        : (defaultsApplied.push('co2_diff'), categoryDefaults.co2Diff);

    const costK =
      guards.is.finiteNumber(parsedCostK) && parsedCostK > 0
        ? parsedCostK
        : (defaultsApplied.push('cost_k'), categoryDefaults.costK);

    const costDiff = costK * co2Diff;

    const limits: MarketEstimateLimits = {
      carbon: {
        min: -co2Diff,
        max: co2Diff,
      },
      cost: {
        min: -costDiff,
        max: +costDiff,
      },
    };

    return [productId, { valid: true, limits, defaultsApplied }];
  });

  _marketEstimateData = Object.fromEntries(entries);
  return _marketEstimateData;
};

/** Get parsed market estimate data, lazily initialized */
export const getMarketEstimateData = (): Record<
  ProductID,
  MarketEstimateResult
> => parseMarketEstimateData();

/** Default limits when no product-specific data is available */
export const DEFAULT_LIMITS: MarketEstimateLimits = (() => {
  const { co2Diff, costK } = MARKET_ESTIMATE_CONFIG.defaultEstimate;
  return {
    carbon: { min: -co2Diff, max: co2Diff },
    cost: { min: -(co2Diff * costK), max: co2Diff * costK },
  };
})();

/**
 * Get limits for a specific product, falling back to defaults.
 */
export const getProductLimits = (
  productId?: ProductID,
): MarketEstimateLimits => {
  if (guards.is.undefined(productId)) return DEFAULT_LIMITS;
  const data = getMarketEstimateData();
  const result = data[productId];
  return result?.valid === true ? result.limits : DEFAULT_LIMITS;
};
